import type { Block, BlockType, LocaleCode } from '@fixit/bozp-content';
import { AccordionBlockView } from './blocks/accordion';
import { AnimationBlockView } from './blocks/animation';
import { CalloutBlockView } from './blocks/callout';
import { ChecklistBlockView } from './blocks/checklist';
import { HeadingBlockView } from './blocks/heading';
import { HotspotImageBlockView } from './blocks/hotspot-image';
import { HotspotSceneBlockView } from './blocks/hotspot-scene';
import { ImageBlockView } from './blocks/image';
import { KeyTakeawayBlockView } from './blocks/key-takeaway';
import { KnowledgeCheckBlockView } from './blocks/knowledge-check';
import { OrderingBlockView } from './blocks/ordering';
import { ParagraphBlockView } from './blocks/paragraph';
import { VideoBlockView } from './blocks/video';

export interface BlockRendererProps {
  block: Block;
  locale?: LocaleCode;
  /**
   * Fires ONCE on the first meaningful interaction of an interactive block
   * (hotspotImage, hotspotScene, ordering, knowledgeCheck, video, accordion); the argument is
   * the block's id. Static blocks never call it. The player (Plan 7) uses this
   * to mark `requiredWidgetIds` complete + emit telemetry; the renderer stores none.
   */
  onInteract?: (widgetId: string) => void;
}

/**
 * The single typed dispatch over a content block. Exhaustive on the
 * discriminated union; an authored-but-unknown blockType renders a defensive
 * placeholder rather than crashing the player. `onInteract` is threaded ONLY to
 * the 5 interactive blocks.
 */
export function BlockRenderer({ block, locale = 'cs-CZ', onInteract }: BlockRendererProps) {
  switch (block.blockType) {
    case 'heading':
      return <HeadingBlockView block={block} locale={locale} />;
    case 'paragraph':
      return <ParagraphBlockView block={block} locale={locale} />;
    case 'image':
      return <ImageBlockView block={block} locale={locale} />;
    case 'callout':
      return <CalloutBlockView block={block} locale={locale} />;
    case 'checklist':
      return <ChecklistBlockView block={block} locale={locale} />;
    case 'keyTakeaway':
      return <KeyTakeawayBlockView block={block} locale={locale} />;
    case 'hotspotImage':
      return <HotspotImageBlockView block={block} locale={locale} onInteract={onInteract} />;
    case 'hotspotScene':
      return <HotspotSceneBlockView block={block} locale={locale} onInteract={onInteract} />;
    case 'ordering':
      return <OrderingBlockView block={block} locale={locale} onInteract={onInteract} />;
    case 'knowledgeCheck':
      return <KnowledgeCheckBlockView block={block} locale={locale} onInteract={onInteract} />;
    case 'video':
      return <VideoBlockView block={block} locale={locale} onInteract={onInteract} />;
    case 'accordion':
      return <AccordionBlockView block={block} locale={locale} onInteract={onInteract} />;
    case 'animation':
      return <AnimationBlockView block={block} locale={locale} />;
    default:
      return (
        <div
          data-block-type="unknown"
          data-unknown-type={(block as { blockType: BlockType }).blockType}
        />
      );
  }
}
